// src/pages/Home.js
// Public homepage — self-contained with its own nav and footer.
// Accessible at /home-preview (admin-only) while Coming Soon is active.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LogoWhite from '../assets/divine-frameworks-logo-white.svg';
import LogoDark from '../assets/divine-frameworks-logo.svg';

const MODALITIES = [
  'Tarot Reading', 'Energy Healing', 'Astrology', 'Life Coaching',
  'Meditation', 'Reiki', 'Breathwork', 'Sound Healing', 'Shamanic Work',
  'Numerology', 'Akashic Records', 'Mediumship', 'Spiritual Coaching',
  'Hypnotherapy', 'Crystal Healing', 'Intuitive Reading', 'Palmistry',
  'Past Life Regression',
];

const SEEKER_FEATURES = [
  { kicker: 'Practitioners you can trust', headline: 'Verified profiles. Real reviews.', supporting: 'We take trust seriously. Every practitioner on Divine Frameworks is identity-verified, manually reviewed by our team, and held to ongoing quality standards. Many go further with background checks and professional evaluations. You\'ll see exactly what each practitioner has earned — so you can book with real confidence, not hope.', placeholder: 'Verification badge preview', reverse: false },
  { kicker: 'Booking made simple', headline: 'Find your time, book in seconds, and you\'re set.', supporting: 'See real-time availability, choose a session that fits your schedule, and pay securely — all in one place. You\'ll get instant confirmation and a reminder before your session so nothing falls through the cracks.', placeholder: 'Booking flow preview', reverse: true },
  { kicker: 'A space built for you', headline: 'Your experience matters as much as theirs.', supporting: 'From discovering the right practitioner to reflecting after your session, every step is designed with your comfort and confidence in mind. This isn\'t a marketplace — it\'s a space where meaningful connections happen.', placeholder: 'Session experience preview', reverse: false },
];

const PRACTITIONER_FEATURES = [
  { kicker: 'Keep what you earn', headline: '100% of your revenue stays with you.', supporting: 'No commissions, no hidden fees. You set your prices, clients pay you directly through Stripe, and every dollar goes into your account. Divine Frameworks makes money from a simple subscription — not from your sessions.', placeholder: 'Revenue dashboard preview', reverse: false },
  { kicker: 'Tools that support your craft', headline: 'Spend your energy on the work that matters.', supporting: 'Let AI handle the prep work — client insights before sessions, thoughtful reflection after, and a smart directory that connects you with seekers who are looking for exactly what you offer.', placeholder: 'AI session prep preview', reverse: true },
  { kicker: 'One home for everything', headline: 'Website. Bookings. Payments. Calendar. Done.', supporting: 'Stop stitching together five different tools. Divine Frameworks brings your website, scheduling, payments, client management, and calendar sync into one place — so you can spend less time on setup and more time with the people you serve.', placeholder: 'All-in-one dashboard preview', reverse: false },
];

const Home = () => {
  const navigate = useNavigate();
  const [audienceMode, setAudienceMode] = useState('seeker');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = React.useRef([]);

  // Scroll detection — toggle nav style at 80px
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Scroll-triggered fade-in animations (ref-based)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => {
              const next = new Set(prev);
              next.add(entry.target.dataset.section);
              return next;
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <Helmet>
        <title>Divine Frameworks — Build Your Spiritual Practice Online</title>
        <meta name="description" content="Divine Frameworks helps spiritual practitioners build a professional online presence, manage bookings, and grow their practice." />
        <meta property="og:title" content="Divine Frameworks — Build Your Spiritual Practice Online" />
        <meta property="og:description" content="Tools for spiritual practitioners to build websites, manage bookings, and grow their practice." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://divineframeworks.com/" />
        <meta property="og:image" content="https://divineframeworks.com/og-default.png" />
        <meta property="og:site_name" content="Divine Frameworks" />
      </Helmet>

      <div style={{ fontFamily: "'Montserrat', sans-serif", overflowX: 'hidden' }}>

        {/* ── Navigation Bar ── */}
        <nav style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          height: '72px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 48px',
          background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : '1px solid transparent',
          boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.04)' : 'none',
          transition: 'all 0.3s ease',
          overflow: 'visible',
        }}>
          {/* Left: Logo + Brand */}
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={scrollToTop}>
            <img
              src={scrolled ? LogoDark : LogoWhite}
              alt="Divine Frameworks"
              style={{
                height: '100px',
                width: 'auto',
                transition: 'opacity 0.3s ease',
              }}
            />
            <span className="df-home-nav-brand-text" style={{
              fontSize: '20px',
              fontWeight: 500,
              letterSpacing: '0.025em',
              textTransform: 'uppercase',
              color: scrolled ? '#000' : '#fff',
              transition: 'color 0.3s ease',
            }}>Divine Frameworks</span>
          </div>

          {/* Center: Nav links (absolutely centered) */}
          <div className="df-home-nav-desktop" style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '32px',
          }}>
            <a
              href="/directory"
              onClick={(e) => { e.preventDefault(); navigate('/directory'); }}
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: scrolled ? '#555' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
              }}
            >Browse Practitioners</a>
            <a
              href="/pricing"
              onClick={(e) => { e.preventDefault(); navigate('/pricing'); }}
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: scrolled ? '#555' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
              }}
            >Pricing</a>
            <a
              href="/about"
              onClick={(e) => { e.preventDefault(); navigate('/about'); }}
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: scrolled ? '#555' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
              }}
            >About</a>
          </div>

          {/* Right: Log in + Get started */}
          <div className="df-home-nav-desktop" style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}>
            <a
              href="/login"
              onClick={(e) => { e.preventDefault(); navigate('/login'); }}
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: scrolled ? '#555' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                transition: 'color 0.3s ease',
              }}
            >Log in</a>
            <button
              onClick={() => navigate('/signup')}
              style={{
                fontSize: '14px',
                fontWeight: 600,
                padding: '10px 24px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                background: scrolled ? '#7c3aed' : '#fff',
                color: scrolled ? '#fff' : '#7c3aed',
                transition: 'all 0.3s ease',
              }}
            >Get started</button>
          </div>

          {/* Mobile controls (visible below 768px) */}
          <div className="df-home-nav-mobile" style={{
            display: 'none',
            alignItems: 'center',
            gap: '12px',
          }}>
            <button
              onClick={() => navigate('/signup')}
              style={{
                fontSize: '13px',
                fontWeight: 600,
                padding: '8px 18px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                background: scrolled ? '#7c3aed' : '#fff',
                color: scrolled ? '#fff' : '#7c3aed',
                transition: 'all 0.3s ease',
              }}
            >Get started</button>
            {/* Hamburger icon */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              {[0, 1, 2].map((i) => (
                <span key={i} style={{
                  display: 'block',
                  width: '22px',
                  height: '2px',
                  borderRadius: '1px',
                  background: scrolled ? '#333' : '#fff',
                  transition: 'background 0.3s ease',
                }} />
              ))}
            </button>
          </div>
        </nav>

        {/* ── Mobile Menu Overlay ── */}
        {mobileMenuOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            padding: '24px 28px',
          }}>
            {/* Close button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '28px',
                  color: '#333',
                  lineHeight: 1,
                  padding: '4px',
                }}
              >&times;</button>
            </div>
            {/* Links */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '36px',
              paddingTop: '48px',
            }}>
              {[
                { label: 'Browse Practitioners', path: '/directory' },
                { label: 'Pricing', path: '/pricing' },
                { label: 'About', path: '/about' },
                { label: 'Log in', path: '/login' },
              ].map(({ label, path }) => (
                <a
                  key={path}
                  href={path}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileMenuOpen(false);
                    navigate(path);
                  }}
                  style={{
                    fontSize: '20px',
                    fontWeight: 500,
                    color: '#222',
                    textDecoration: 'none',
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >{label}</a>
              ))}
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/signup'); }}
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '14px 32px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  background: '#7c3aed',
                  color: '#fff',
                  alignSelf: 'flex-start',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >Get started</button>
            </div>
          </div>
        )}

        {/* ── Hero Section ── */}
        <section style={{
          position: 'relative',
          minHeight: '85vh',
          marginTop: '-72px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '120px 32px 64px',
          textAlign: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
          backgroundSize: '400% 400%',
          animation: 'aurora 18s ease infinite',
        }}>
          {/* Dark overlay */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(15, 10, 30, 0.3)',
            zIndex: 0,
          }} />

          {/* Hero content */}
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '860px' }}>

            {/* Toggle */}
            <div style={{
              display: 'inline-flex',
              background: 'rgba(255,255,255,0.12)',
              borderRadius: '30px',
              padding: '4px',
              border: '1px solid rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              marginBottom: '40px',
            }}>
              {['seeker', 'practitioner'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setAudienceMode(mode)}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '26px',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    background: audienceMode === mode ? '#fff' : 'transparent',
                    color: audienceMode === mode ? '#7c3aed' : 'rgba(255,255,255,0.6)',
                    boxShadow: audienceMode === mode ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {mode === 'seeker' ? 'Find a practitioner' : "I'm a practitioner"}
                </button>
              ))}
            </div>

            {/* Headline */}
            <h1 style={{
              fontSize: 'clamp(28px, 5.5vw, 48px)',
              fontWeight: 700,
              lineHeight: 1.15,
              color: '#fff',
              margin: '0 0 28px',
            }}>
              Trusted practitioners.<br />Meaningful sessions.
            </h1>

            {/* Subline — swap with fade */}
            <div style={{ position: 'relative', minHeight: '80px', marginBottom: '44px' }}>
              <p style={{
                fontSize: 'clamp(14px, 2.5vw, 18px)',
                fontWeight: 400,
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.85)',
                maxWidth: '620px',
                margin: '0 auto',
                opacity: audienceMode === 'seeker' ? 1 : 0,
                transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                position: audienceMode === 'seeker' ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
              }}>
                Discover experienced practitioners who understand your journey. Browse by modality, read real reviews, and book a session that feels right for you.
              </p>
              <p style={{
                fontSize: 'clamp(14px, 2.5vw, 18px)',
                fontWeight: 400,
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.85)',
                maxWidth: '620px',
                margin: '0 auto',
                opacity: audienceMode === 'practitioner' ? 1 : 0,
                transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
              }}>
                Everything you need to run your practice professionally — a beautiful website, seamless bookings, secure payments, and AI-powered insights to help you thrive.
              </p>
            </div>

            {/* CTA — swap with fade */}
            <div style={{ position: 'relative', minHeight: '52px', display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => navigate('/directory')}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  fontWeight: 600,
                  padding: '16px 40px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  background: '#bb9749',
                  color: '#fff',
                  boxShadow: '0 4px 16px rgba(187, 151, 73, 0.3)',
                  letterSpacing: '0.3px',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                  opacity: audienceMode === 'seeker' ? 1 : 0,
                  transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
                  position: 'absolute',
                  top: 0,
                  pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(187, 151, 73, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(187, 151, 73, 0.3)'; }}
              >Explore practitioners</button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px',
                  fontWeight: 600,
                  padding: '16px 40px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  background: '#bb9749',
                  color: '#fff',
                  boxShadow: '0 4px 16px rgba(187, 151, 73, 0.3)',
                  letterSpacing: '0.3px',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                  opacity: audienceMode === 'practitioner' ? 1 : 0,
                  transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
                  position: 'absolute',
                  top: 0,
                  pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(187, 151, 73, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(187, 151, 73, 0.3)'; }}
              >Get started free</button>
            </div>
          </div>

          {/* Modality Ticker */}
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            overflow: 'hidden',
            marginTop: '44px',
            maskImage: 'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              width: 'max-content',
              animation: 'ticker 35s linear infinite',
            }}>
              {[...MODALITIES, ...MODALITIES].map((mod, i) => (
                <span
                  key={i}
                  onClick={() => navigate('/directory')}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: 'rgba(255,255,255,0.75)',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    backdropFilter: 'blur(4px)',
                    WebkitBackdropFilter: 'blur(4px)',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                  }}
                >{mod}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 2: How It Works (swaps on toggle) ── */}
        <section className="df-home-how-it-works" data-section="section-2" ref={(el) => (sectionRefs.current[0] = el)} style={{
          background: '#fff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-2') ? 1 : 0,
          transform: visibleSections.has('section-2') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
            {/* Seeker version */}
            <div style={{
              opacity: audienceMode === 'seeker' ? 1 : 0,
              transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'seeker' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
            }}>
              <p style={{
                fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
                textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
              }}>How it works</p>
              <h2 style={{
                fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700,
                color: '#1a1a2e', margin: '0 0 56px',
              }}>From curiosity to connection in three steps.</h2>
              <div className="df-home-steps" style={{
                display: 'flex', gap: '40px', justifyContent: 'center',
              }}>
                {[
                  { num: '1', title: 'Discover', desc: 'Browse practitioners by service, location, or availability. Read reviews from real clients and find someone who feels right for you.' },
                  { num: '2', title: 'Book', desc: 'Choose a time that works, pay securely, and get instant confirmation. The whole process takes less than a minute.' },
                  { num: '3', title: 'Experience', desc: 'Connect with your practitioner via video or in person. Every session is supported by a platform built for meaningful work.' },
                ].map((step) => (
                  <div key={step.num} style={{ maxWidth: '300px', textAlign: 'center' }}>
                    <div style={{
                      width: '52px', height: '52px', borderRadius: '50%',
                      background: '#faf5eb', color: '#bb9749',
                      fontSize: '20px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}>{step.num}</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1a1a2e', marginBottom: '8px' }}>{step.title}</h3>
                    <p style={{ fontSize: '15px', fontWeight: 400, lineHeight: 1.7, color: '#666', margin: 0 }}>{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Practitioner version */}
            <div style={{
              opacity: audienceMode === 'practitioner' ? 1 : 0,
              transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
            }}>
              <p style={{
                fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
                textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
              }}>How it works</p>
              <h2 style={{
                fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700,
                color: '#1a1a2e', margin: '0 0 56px',
              }}>From setup to sessions in three steps.</h2>
              <div className="df-home-steps" style={{
                display: 'flex', gap: '40px', justifyContent: 'center',
              }}>
                {[
                  { num: '1', title: 'Build', desc: 'Create a professional website, set up your services, and define your availability. Your online presence comes together in minutes.' },
                  { num: '2', title: 'Connect', desc: 'Clients discover you in the directory, book directly, and pay securely. You get instant notifications and calendar sync.' },
                  { num: '3', title: 'Grow', desc: 'Use AI-powered session prep, reflective coaching tools, and client insights to deepen your practice and serve with confidence.' },
                ].map((step) => (
                  <div key={step.num} style={{ maxWidth: '300px', textAlign: 'center' }}>
                    <div style={{
                      width: '52px', height: '52px', borderRadius: '50%',
                      background: '#faf5eb', color: '#bb9749',
                      fontSize: '20px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}>{step.num}</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1a1a2e', marginBottom: '8px' }}>{step.title}</h3>
                    <p style={{ fontSize: '15px', fontWeight: 400, lineHeight: 1.7, color: '#666', margin: 0 }}>{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 3: Product Showcase (swaps on toggle) ── */}
        <section className="df-home-showcase-section" data-section="section-3" ref={(el) => (sectionRefs.current[1] = el)} style={{
          background: '#faf9ff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-3') ? 1 : 0,
          transform: visibleSections.has('section-3') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative' }}>
            {/* Seeker version */}
            <div className="df-home-showcase" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '64px',
              opacity: audienceMode === 'seeker' ? 1 : 0,
              transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'seeker' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
            }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Guidance you can feel good about</p>
                <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 32px)', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 20px' }}>Find practitioners who are as invested in your journey as you are.</h2>
                <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: 0 }}>Every practitioner on Divine Frameworks is verified and held to a standard. Our multi-level trust system means you always know who you're booking with — browse profiles, check their credentials, read real reviews, and book with confidence.</p>
              </div>
              <div style={{
                flex: '0 0 auto', width: '100%', maxWidth: '500px', height: '340px',
                background: 'linear-gradient(135deg, #e8dff5, #dbeafe)', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Practitioner profile preview</span>
              </div>
            </div>

            {/* Practitioner version */}
            <div className="df-home-showcase" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '64px',
              opacity: audienceMode === 'practitioner' ? 1 : 0,
              transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
            }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Your practice, elevated</p>
                <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 32px)', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 20px' }}>Focus on your clients. We'll handle the rest.</h2>
                <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: 0 }}>Divine Frameworks gives you a professional home for your practice — a beautiful website, seamless bookings, and secure payments, all in one place. Less time on logistics, more time doing the work you love.</p>
              </div>
              <div style={{
                flex: '0 0 auto', width: '100%', maxWidth: '500px', height: '340px',
                background: 'linear-gradient(135deg, #e8dff5, #dbeafe)', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: '14px', color: '#888' }}>Website builder mockup</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Section 4: Feature Highlights (swaps on toggle) ── */}
        <section className="df-home-features" data-section="section-4" ref={(el) => (sectionRefs.current[2] = el)} style={{
          background: '#fff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-4') ? 1 : 0,
          transform: visibleSections.has('section-4') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative' }}>
            {/* Seeker version */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '80px',
              opacity: audienceMode === 'seeker' ? 1 : 0,
              transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'seeker' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
            }}>
              {SEEKER_FEATURES.map((f, i) => (
                <div key={i} className="df-home-features-block" style={{
                  display: 'flex', alignItems: 'center', gap: '64px',
                  flexDirection: f.reverse ? 'row-reverse' : 'row',
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>{f.kicker}</p>
                    <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 32px)', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 20px' }}>{f.headline}</h2>
                    <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: 0 }}>{f.supporting}</p>
                  </div>
                  <div style={{
                    flex: '0 0 auto', width: '100%', maxWidth: '480px', height: '300px',
                    background: 'linear-gradient(135deg, #e8dff5, #dbeafe)', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '14px', color: '#888' }}>{f.placeholder}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Practitioner version */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '80px',
              opacity: audienceMode === 'practitioner' ? 1 : 0,
              transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
            }}>
              {PRACTITIONER_FEATURES.map((f, i) => (
                <div key={i} className="df-home-features-block" style={{
                  display: 'flex', alignItems: 'center', gap: '64px',
                  flexDirection: f.reverse ? 'row-reverse' : 'row',
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>{f.kicker}</p>
                    <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 32px)', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 20px' }}>{f.headline}</h2>
                    <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: 0 }}>{f.supporting}</p>
                  </div>
                  <div style={{
                    flex: '0 0 auto', width: '100%', maxWidth: '480px', height: '300px',
                    background: 'linear-gradient(135deg, #e8dff5, #dbeafe)', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '14px', color: '#888' }}>{f.placeholder}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 5: Social Proof ── */}
        <section className="df-home-stats-section" data-section="section-5" ref={(el) => (sectionRefs.current[3] = el)} style={{
          background: '#faf9ff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-5') ? 1 : 0,
          transform: visibleSections.has('section-5') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            <p style={{
              fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px',
              textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px',
            }}>By the numbers</p>
            <h2 style={{
              fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700,
              color: '#1a1a2e', margin: '0 0 56px',
            }}>Built to earn your trust.</h2>

            <div className="df-home-stats" style={{
              display: 'flex', gap: '24px', justifyContent: 'center',
            }}>
              {[
                { number: '50+', label: 'Services represented' },
                { number: '100%', label: 'Revenue kept by practitioners' },
                { number: '0%', label: 'Commissions. Ever.' },
              ].map((stat, i) => (
                <div key={i} className="df-home-stat-card" style={{
                  background: '#fff', borderRadius: '12px', padding: '32px 24px',
                  flex: 1, maxWidth: '280px', textAlign: 'center',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}>
                  <div style={{
                    fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700,
                    color: '#1a1a2e', marginBottom: '8px',
                  }}>{stat.number}</div>
                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#666' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 6: Directory Preview ── */}
        <section className="df-home-directory" data-section="section-6" ref={(el) => (sectionRefs.current[4] = el)} style={{
          background: '#fff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-6') ? 1 : 0,
          transform: visibleSections.has('section-6') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
            {/* Header — swaps on toggle */}
            <div style={{ position: 'relative' }}>
              <div style={{
                opacity: audienceMode === 'seeker' ? 1 : 0,
                transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                position: audienceMode === 'seeker' ? 'relative' : 'absolute',
                top: 0, left: 0, right: 0,
                pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
              }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Explore the community</p>
                <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' }}>Meet practitioners who are ready for you.</h2>
                <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', maxWidth: '560px', margin: '0 auto 40px', textAlign: 'center' }}>Every practitioner you see here has been verified. Look for trust badges to find practitioners who've gone above and beyond.</p>
              </div>
              <div style={{
                opacity: audienceMode === 'practitioner' ? 1 : 0,
                transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
                top: 0, left: 0, right: 0,
                pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
              }}>
                <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Your spotlight awaits</p>
                <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: '#1a1a2e', margin: '0 0 56px' }}>You do the work. We bring the people who need it.</h2>
              </div>
            </div>

            <div className="df-home-directory-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '24px',
              maxWidth: '1000px',
              margin: '0 auto',
            }}>
              {[
                { name: 'Sarah Mitchell', services: 'Energy Healing · Reiki', initials: 'SM' },
                { name: 'James Rivera', services: 'Tarot Reading · Astrology', initials: 'JR' },
                { name: 'Luna Chen', services: 'Life Coaching · Meditation', initials: 'LC' },
                { name: 'Alex Thompson', services: 'Sound Healing · Breathwork', initials: 'AT' },
                { name: 'Maya Patel', services: 'Spiritual Coaching · Numerology', initials: 'MP' },
                { name: 'Daniel Flores', services: 'Hypnotherapy · Past Life Regression', initials: 'DF' },
              ].map((card) => (
                <div
                  key={card.name}
                  onClick={() => navigate('/directory')}
                  style={{
                    background: '#fff',
                    border: '1px solid #eee',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Card image area */}
                  <div style={{
                    height: '180px',
                    background: 'linear-gradient(135deg, #f3f0ff, #eef2ff)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{
                      width: '72px', height: '72px', borderRadius: '50%',
                      background: '#e2dff0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '24px', color: '#999', fontWeight: 600,
                    }}>{card.initials}</div>
                  </div>
                  {/* Card info */}
                  <div style={{ padding: '16px', textAlign: 'left' }}>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e', marginBottom: '4px' }}>{card.name}</div>
                    <div style={{ fontSize: '13px', fontWeight: 400, color: '#888', marginBottom: '8px' }}>{card.services}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#bb9749', letterSpacing: '1px' }}>★★★★★</span>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#555' }}>5.0</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom link — swaps on toggle */}
            <div style={{ marginTop: '40px', textAlign: 'center', position: 'relative' }}>
              <a
                href="/directory"
                onClick={(e) => { e.preventDefault(); navigate('/directory'); }}
                style={{
                  fontSize: '15px', fontWeight: 600, color: '#7c3aed',
                  textDecoration: 'none', cursor: 'pointer',
                  opacity: audienceMode === 'seeker' ? 1 : 0,
                  transition: 'opacity 0.4s ease',
                  position: audienceMode === 'seeker' ? 'relative' : 'absolute',
                  pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >Browse all practitioners →</a>
              <a
                href="/signup"
                onClick={(e) => { e.preventDefault(); navigate('/signup'); }}
                style={{
                  fontSize: '15px', fontWeight: 600, color: '#7c3aed',
                  textDecoration: 'none', cursor: 'pointer',
                  opacity: audienceMode === 'practitioner' ? 1 : 0,
                  transition: 'opacity 0.4s ease',
                  position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
                  pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >Join the directory →</a>
            </div>
          </div>
        </section>

        {/* ── Section 7: Pricing Preview (swaps on toggle) ── */}
        <section className="df-home-pricing" data-section="section-7" ref={(el) => (sectionRefs.current[5] = el)} style={{
          background: '#faf9ff',
          padding: '96px 32px',
          opacity: visibleSections.has('section-7') ? 1 : 0,
          transform: visibleSections.has('section-7') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}>
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
            {/* Seeker version */}
            <div style={{
              opacity: audienceMode === 'seeker' ? 1 : 0,
              transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'seeker' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
            }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Ready when you are</p>
              <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px' }}>Your next session is just a few clicks away.</h2>
              <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: '0 0 36px' }}>No account required to browse. Signing up is free, and booking is simple and secure. Take your time — the right practitioner is here when you're ready.</p>
              <button
                onClick={() => navigate('/directory')}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px', fontWeight: 600,
                  padding: '14px 32px', borderRadius: '8px',
                  border: 'none', cursor: 'pointer',
                  background: '#7c3aed', color: '#fff',
                  transition: 'transform 0.2s ease',
                }}
              >Browse practitioners →</button>
            </div>

            {/* Practitioner version */}
            <div style={{
              opacity: audienceMode === 'practitioner' ? 1 : 0,
              transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
            }}>
              <p style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#bb9749', marginBottom: '12px' }}>Simple, transparent pricing</p>
              <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: '#1a1a2e', margin: '0 0 20px' }}>Start free. Grow on your terms.</h2>
              <p style={{ fontSize: '16px', fontWeight: 400, lineHeight: 1.7, color: '#555', margin: '0 0 36px' }}>Plans from $0 to $99/month — no commissions, no surprises. Choose the tier that fits where you are today, and upgrade whenever you're ready.</p>
              <button
                onClick={() => navigate('/pricing')}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px', fontWeight: 600,
                  padding: '14px 32px', borderRadius: '8px',
                  border: 'none', cursor: 'pointer',
                  background: '#7c3aed', color: '#fff',
                  transition: 'transform 0.2s ease',
                }}
              >View pricing →</button>
            </div>
          </div>
        </section>

        {/* ── Section 8: Final CTA (swaps on toggle) ── */}
        <section data-section="section-8" ref={(el) => (sectionRefs.current[6] = el)} style={{
          position: 'relative',
          overflow: 'hidden',
          padding: '96px 32px',
          opacity: visibleSections.has('section-8') ? 1 : 0,
          transform: visibleSections.has('section-8') ? 'translateY(0)' : 'translateY(60px)',
          transition: 'opacity 1s cubic-bezier(0.25, 0.1, 0.25, 1), transform 1s cubic-bezier(0.25, 0.1, 0.25, 1)',
          textAlign: 'center',
          background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
          backgroundSize: '400% 400%',
          animation: 'aurora 18s ease infinite',
        }}>
          {/* Dark overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(15, 10, 30, 0.3)', zIndex: 0,
          }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: '700px', margin: '0 auto' }}>
            {/* Seeker version */}
            <div style={{
              opacity: audienceMode === 'seeker' ? 1 : 0,
              transform: audienceMode === 'seeker' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'seeker' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'seeker' ? 'auto' : 'none',
            }}>
              <h2 style={{ fontSize: 'clamp(24px, 4.5vw, 40px)', fontWeight: 700, color: '#fff', margin: '0 0 20px' }}>Your journey starts with the right connection.</h2>
              <p style={{ fontSize: 'clamp(14px, 2.5vw, 18px)', fontWeight: 400, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', maxWidth: '560px', margin: '0 auto 36px' }}>Browse verified practitioners, read real reviews, and book a session that feels right for you. No pressure, no commitment — just a space for meaningful work.</p>
              <button
                onClick={() => navigate('/directory')}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(187, 151, 73, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(187, 151, 73, 0.3)'; }}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px', fontWeight: 600,
                  padding: '16px 40px', borderRadius: '8px',
                  border: 'none', cursor: 'pointer',
                  background: '#bb9749', color: '#fff',
                  boxShadow: '0 4px 16px rgba(187, 151, 73, 0.3)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >Browse practitioners</button>
              <div style={{ marginTop: '20px' }}>
                <a
                  href="/signup"
                  onClick={(e) => { e.preventDefault(); navigate('/signup'); }}
                  style={{
                    fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none', cursor: 'pointer',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                >I'm a practitioner →</a>
              </div>
            </div>

            {/* Practitioner version */}
            <div style={{
              opacity: audienceMode === 'practitioner' ? 1 : 0,
              transform: audienceMode === 'practitioner' ? 'translateY(0)' : 'translateY(8px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
              position: audienceMode === 'practitioner' ? 'relative' : 'absolute',
              top: 0, left: 0, right: 0,
              pointerEvents: audienceMode === 'practitioner' ? 'auto' : 'none',
            }}>
              <h2 style={{ fontSize: 'clamp(24px, 4.5vw, 40px)', fontWeight: 700, color: '#fff', margin: '0 0 20px' }}>Your practice deserves this.</h2>
              <p style={{ fontSize: 'clamp(14px, 2.5vw, 18px)', fontWeight: 400, lineHeight: 1.7, color: 'rgba(255,255,255,0.85)', maxWidth: '560px', margin: '0 auto 36px' }}>Join a platform built for practitioners who take their work seriously. Start free, set up in minutes, and focus on what you do best.</p>
              <button
                onClick={() => navigate('/signup')}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(187, 151, 73, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(187, 151, 73, 0.3)'; }}
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '15px', fontWeight: 600,
                  padding: '16px 40px', borderRadius: '8px',
                  border: 'none', cursor: 'pointer',
                  background: '#bb9749', color: '#fff',
                  boxShadow: '0 4px 16px rgba(187, 151, 73, 0.3)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >Get started free</button>
              <div style={{ marginTop: '20px' }}>
                <a
                  href="/directory"
                  onClick={(e) => { e.preventDefault(); navigate('/directory'); }}
                  style={{
                    fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none', cursor: 'pointer',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                >Browse practitioners →</a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{
          background: '#111',
          padding: '48px 32px 32px',
        }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            {/* Top row */}
            <div className="df-home-footer-top" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            }}>
              {/* Logo + tagline */}
              <div>
                <img src={LogoWhite} alt="Divine Frameworks" style={{ height: '100px', width: 'auto' }} />
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>Where the journey begins.</p>
              </div>

              {/* Link columns */}
              <div className="df-home-footer-columns" style={{ display: 'flex', gap: '64px' }}>
                {/* Platform */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Platform</p>
                  {[
                    { label: 'Browse Practitioners', path: '/directory' },
                    { label: 'Pricing', path: '/pricing' },
                    { label: 'About', path: '/about' },
                  ].map(({ label, path }) => (
                    <a
                      key={path}
                      href={path}
                      onClick={(e) => { e.preventDefault(); navigate(path); }}
                      style={{
                        display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)',
                        textDecoration: 'none', marginBottom: '10px', transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                    >{label}</a>
                  ))}
                </div>

                {/* Support */}
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Support</p>
                  {[
                    { label: 'Contact Us', href: 'mailto:support@divineframeworks.com' },
                    { label: 'Privacy Policy', href: '#' },
                    { label: 'Terms of Service', href: '#' },
                  ].map(({ label, href, path }) => (
                    <a
                      key={label}
                      href={path || href}
                      onClick={path ? (e) => { e.preventDefault(); navigate(path); } : href === '#' ? (e) => e.preventDefault() : undefined}
                      style={{
                        display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.5)',
                        textDecoration: 'none', marginBottom: '10px', transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                    >{label}</a>
                  ))}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ marginTop: '40px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
              {/* Bottom row */}
              <div className="df-home-footer-bottom" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>© 2026 Divine Frameworks. All rights reserved.</span>
                <div style={{ display: 'flex', gap: '20px' }}>
                  {[
                    { name: 'Instagram', svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
                    { name: 'Facebook', svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385h-3.047v-3.47h3.047v-2.642c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385c5.738-.9 10.126-5.864 10.126-11.854z"/></svg> },
                    { name: 'LinkedIn', svg: <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.554v-11.452h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zm-15.11-13.019c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019h-3.564v-11.452h3.564v11.452zm15.106-20.452h-20.454c-.979 0-1.771.774-1.771 1.729v20.542c0 .956.792 1.729 1.771 1.729h20.451c.978 0 1.774-.773 1.774-1.729v-20.542c0-.955-.796-1.729-1.774-1.729z"/></svg> },
                  ].map(({ name, svg }) => (
                    <a
                      key={name}
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      aria-label={name}
                      style={{
                        color: 'rgba(255,255,255,0.5)',
                        textDecoration: 'none', transition: 'color 0.2s ease',
                        display: 'flex', alignItems: 'center',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                    >{svg}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* ── Keyframes + Responsive ── */}
        <style>{`
          @keyframes aurora {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @media (max-width: 767px) {
            nav { padding: 0 20px !important; }
            .df-home-nav-desktop { display: none !important; }
            .df-home-nav-mobile { display: flex !important; }
            .df-home-nav-brand-text { display: none !important; }
            .df-home-how-it-works { padding: 64px 20px !important; }
            .df-home-steps { flex-direction: column !important; align-items: center !important; }
            .df-home-showcase-section { padding: 64px 20px !important; }
            .df-home-showcase { flex-direction: column !important; text-align: center !important; gap: 40px !important; }
            .df-home-features { padding: 64px 20px !important; }
            .df-home-features-block { flex-direction: column !important; text-align: center !important; gap: 32px !important; }
            .df-home-stats-section { padding: 64px 20px !important; }
            .df-home-directory { padding: 64px 20px !important; }
            .df-home-directory-grid { grid-template-columns: 1fr !important; }
            .df-home-pricing { padding: 64px 20px !important; }
            .df-home-stats { gap: 16px !important; }
            .df-home-stat-card { padding: 24px 16px !important; }
            .df-home-footer-top { flex-direction: column !important; gap: 32px !important; }
            .df-home-footer-columns { gap: 32px !important; }
            .df-home-footer-bottom { flex-direction: column !important; text-align: center !important; }
          }
        `}</style>
      </div>
    </>
  );
};

export default Home;
